import stringToHashCode from '@chama/utils/functions/string-to-hash';
import keys from 'lodash.keys';
import values from 'lodash.values';
import {
  FETCH_DEALER_DATA,
  ADD_DEALER_DATA,
  UPDATE_SEVERAL_DEALERS,
  UPDATE_DEALER_DATA,
  DEALER_LOADING,
  ADD_FILTER,
  REMOVE_FILTER,
  GET_NEARBY_DEALERS,
} from '@app/actions/dealers';
import { LOGOUT } from '@app/actions/user';
import { isBlocked, isSuspended } from '@chama/utils/functions/is-dealer-blocked-or-suspended';

const MAX_NUMBER_OF_BUCKETS = 10;
const initialState = {
  all: {},
  filteredAll: {},
  termsAndConditions: undefined,
  isFetching: false,
  filters: [],
  numberOfBuckets: 0,
  nearby: {
    lat: undefined,
    lon: undefined,
    dict: {},
  },
  suspended: [],
  blocked: [],
};

const createDealerDictionary = dealerList => {
  const reduceDealers = (accum, dealer) => {
    return {
      ...accum,
      [dealer.id]: dealer,
    };
  };

  return dealerList.reduce(reduceDealers, {});
};

const getActiveDealers = (activeFilters, dividedDealerList) => {
  const reduceActiveDealers = (accum, filter) => {
    const bucket = dividedDealerList[filter];

    return [...accum, ...bucket];
  };

  return activeFilters.reduce(reduceActiveDealers, []);
};

const cleanActiveFilters = (activeFilters, oldNumberOfBuckets, newNumberOfBuckets) => {
  const newActiveFilters = {
    ...activeFilters,
  };
  let removeFilter = oldNumberOfBuckets - newNumberOfBuckets;

  while (removeFilter < oldNumberOfBuckets) {
    if (newActiveFilters.indexOf(removeFilter) !== -1) {
      newActiveFilters.splice(newActiveFilters.indexOf(removeFilter), 1);
    }

    removeFilter += 1;
  }

  return newActiveFilters;
};

const divideDealers = (dealerList = [], numberOfBuckets = 0, activeFilters = []) => {
  let newDividedDealersList = [];
  const fillDealerList = dealer => {
    // Transform 'dealerId + dealerId + dealerId' to an integer.
    const dealerHashCode = stringToHashCode(`${dealer.id}${dealer.id}${dealer.id}`);
    // Assign each dealer to a 'filter bucket' based on its 'hashCode'.
    let filterBucket = dealerHashCode % MAX_NUMBER_OF_BUCKETS;
    // If 'filterBucket' is a negative integer. We'll transform in into a positive integer.
    filterBucket = filterBucket < 0 ? MAX_NUMBER_OF_BUCKETS + filterBucket : filterBucket;

    newDividedDealersList[filterBucket].push(dealer);
  };

  // Push empty 'buckets' to 'newDividedDealersList'.
  for (let i = 0; i < MAX_NUMBER_OF_BUCKETS; i += 1) {
    newDividedDealersList.push([]);
  }

  // We're now going to fill the empty 'buckets' with dealers.
  dealerList.forEach(fillDealerList);

  // Remove 'buckets' that are still empty.
  newDividedDealersList = newDividedDealersList.filter(bucket => bucket.length);

  /**
   * Update the number of filter buckets,
   * when we've created a new divided dealers list.
   *
   * @event FilterTicketsAgent#numberOfBucketsChanged
   * @private
   */
  const oldNumberOfBuckets = numberOfBuckets;
  const newNumberOfBuckets = newDividedDealersList.length;
  const filteredDealerList = activeFilters.length
    ? getActiveDealers(activeFilters, newDividedDealersList)
    : dealerList;

  // Clean our 'active filters' if we have fewer filter buckets than before.
  if (newNumberOfBuckets < oldNumberOfBuckets) {
    return {
      filteredAll: createDealerDictionary(filteredDealerList),
      numberOfBuckets: newNumberOfBuckets,
      filters: cleanActiveFilters(dealerList, oldNumberOfBuckets, newNumberOfBuckets),
    };
  }

  return {
    filteredAll: createDealerDictionary(filteredDealerList),
    numberOfBuckets: newNumberOfBuckets,
    filters: activeFilters,
  };
};

const updateSeveralDealers = (currentStateDealersDict, updatedDealerList) => {
  const dealerInArray = dealerId => dealer => dealer.id === dealerId;
  const toUpdateDealers = (accum, dealerId) => {
    const updatedCurrentDealer = updatedDealerList.find(dealerInArray(dealerId));

    if (updatedCurrentDealer) {
      return {
        ...accum,
        [dealerId]: {
          ...currentStateDealersDict[dealerId],
          ...updatedCurrentDealer,
        },
      };
    }

    return accum;
  };

  if (updatedDealerList.length) {
    return keys(currentStateDealersDict).reduce(toUpdateDealers, currentStateDealersDict);
  }

  return currentStateDealersDict;
};

const groupIntoSuspendedOrBlocked = (dealers, { suspended = [], blocked = [] }) =>
  dealers.reduce(
    (acc, item) => {
      if (isBlocked(item)) {
        if (!blocked.includes(item.id)) {
          return {
            ...acc,
            suspended: acc.suspended.filter(suspendedItem => suspendedItem !== item.id),
            blocked: acc.blocked.concat(item.id),
          };
        }
        return acc;
      }
      if (isSuspended(item)) {
        if (!suspended.includes(item.id)) {
          return {
            ...acc,
            suspended: acc.suspended.concat(item.id),
            blocked: acc.blocked.filter(blockedItem => blockedItem !== item.id),
          };
        }
        return acc;
      }
      return {
        ...acc,
        suspended: acc.suspended.filter(suspendedItem => suspendedItem !== item.id),
        blocked: acc.blocked.filter(blockedItem => blockedItem !== item.id),
      };
    },
    { suspended, blocked },
  );

const dealer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH_DEALER_DATA:
      return {
        ...state,
        ...divideDealers(action.dealers, state.numberOfBuckets, state.filters),
        all: createDealerDictionary(action.dealers),
        isFetching: false,
        ...groupIntoSuspendedOrBlocked(action.dealers, {
          suspended: state.suspended,
          blocked: state.blocked,
        }),
      };

    case ADD_DEALER_DATA: {
      const newDealerList = [action.dealer, ...Object.values(state.all)];
      return {
        ...state,
        ...divideDealers(newDealerList, state.numberOfBuckets, state.filters),
        all: createDealerDictionary(newDealerList),
        isFetching: false,
        ...groupIntoSuspendedOrBlocked(newDealerList, {
          suspended: state.suspended,
          blocked: state.blocked,
        }),
      };
    }

    case UPDATE_SEVERAL_DEALERS:
      return {
        ...state,
        all: updateSeveralDealers(state.all, action.updatedDealerList),
        filteredAll: updateSeveralDealers(state.filteredAll, action.updatedDealerList),
        isFetching: false,
        ...groupIntoSuspendedOrBlocked(action.updatedDealerList, {
          suspended: state.suspended,
          blocked: state.blocked,
        }),
      };

    case UPDATE_DEALER_DATA:
      return {
        ...state,
        all: {
          ...state.all,
          [action.updatedDealer.id]: {
            ...state.all[action.updatedDealer.id],
            ...action.updatedDealer,
          },
        },
        filteredAll: {
          ...state.filteredAll,
          [action.updatedDealer.id]: {
            ...state.filteredAll[action.updatedDealer.id],
            ...action.updatedDealer,
          },
        },
        isFetching: false,
        ...groupIntoSuspendedOrBlocked([action.updatedDealer], {
          suspended: state.suspended,
          blocked: state.blocked,
        }),
      };

    case DEALER_LOADING:
      return {
        ...state,
        isFetching: true,
      };

    case GET_NEARBY_DEALERS:
      return {
        ...state,
        nearby: {
          lat: action.lat,
          lon: action.lon,
          dict: createDealerDictionary(action.dealers),
        },
      };

    case LOGOUT:
      return {
        ...initialState,
      };

    case ADD_FILTER:
      return {
        ...state,
        ...divideDealers(values(state.all), state.numberOfBuckets, [
          ...state.filters,
          action.number,
        ]),
      };

    case REMOVE_FILTER:
      return {
        ...state,
        ...divideDealers(values(state.all), state.numberOfBuckets, [
          ...state.filters.slice(0, action.indexOfFilter),
          ...state.filters.slice(action.indexOfFilter + 1),
        ]),
      };

    default:
      return state;
  }
};

export default dealer;



// WEBPACK FOOTER //
// ./src/reducer/dealer.js